<template>
  <modal-vue :modalActive="visible" min-height="min(700px, 90vh)" @close="onClose">
    <template #header>
      <p class="font-bold">
        {{ edit ? 'Editar política de pagamento' : 'Criar política de pagamento' }}
      </p>
    </template>

    <div class="relative">
      <Spinner
        v-if="isSubmitting"
        :color="'primary'"
        :size="'2rem'"
        :width="'0.5rem'"
        class="absolute inset-0 flex justify-center items-center bg-white bg-opacity-50 z-50"
      />

      <div class="mb-2 flex flex-row justify-between">
        <div class="flex flex-row">
          <div v-if="!isEditing" class="flex gap-2">
            <div>
              <div class="flex items-center">
                <h1 class="font-bold text-left text-black text-2xl">{{ tempPolicyName }}</h1>

                <v-icon
                  :icon="mdiPencilOutline"
                  class="cursor-pointer text-primary ml-1"
                  size="20"
                  @click="startEditing"
                />
              </div>

              <p class="text-start text-sm capitalize">{{ breadcrumb.toUpperCase() }}</p>
            </div>
          </div>
          <div v-else class="flex items-center gap-3">
            <input
              v-model="tempPolicyName"
              ref="nameInput"
              class="border rounded-md px-2 py-1 w-auto"
              placeholder="Nome da política"
              @keydown.enter.prevent="saveEdit"
              @keydown.esc.prevent="cancelEdit"
              @blur="saveEdit"
            />
            <div class="flex gap-4">
              <v-icon
                :icon="mdiCheck"
                class="cursor-pointer text-green-600"
                size="25"
                @click="saveEdit"
              />
              <v-icon
                :icon="mdiClose"
                class="cursor-pointer text-red-600"
                size="25"
                @click="cancelEdit"
              />
            </div>
          </div>
        </div>

        <outlined-button
          @click="store.addRule()"
          :text="'Adicionar regra'"
          height="32px"
          :append-icon="mdiPlus"
          class="text-sm !w-auto !px-4 !py-1"
        />
      </div>

      <!-- Trava do Pós-Plantão (RN-07 / RF-12). Banner persistente além do toast:
           editar a política reescreve TODAS as regras dela, então o backend
           recusa quando a cobertura alcança plantão já aprovado — e o usuário
           precisa da frase (com o período afetado) na tela para decidir entre
           reverter a validação ou restringir a vigência. O formulário é
           preservado: nenhum `emit` de sucesso acontece no erro. -->
      <div
        v-if="postStationLockMessage"
        class="flex items-start gap-2 px-3 py-2 my-3 rounded-md border border-primary30 bg-primary5 text-sm text-textPrimary text-left"
        data-testid="policy-payment-modal-post-station-lock-banner"
      >
        <SVGIcon :path="mdiLock" class="w-4 h-4 mt-0.5 shrink-0 text-primary30" />
        <span>{{ postStationLockMessage }}</span>
      </div>

      <p class="text-sm text-left font-bold text-black my-2">
        Vigência do vínculo entre escala e política de pagamento
      </p>

      <PolicyVigencyFields
        v-model:start-date="step4.startDate"
        v-model:end-date="step4.endDate"
        v-model:indefinite-end-date="step4.indefiniteEndDate"
        :max-date="scheduleMaxDate"
        :errors="vigencyErrors"
        testid-prefix="payment-policy-modal-vigency"
        @clear-error="clearVigencyError"
      />

      <PaymentPolicyRulesTable
        :rules="step4.rules"
        :shifts="step2.shifts"
        :positions="step2.positions"
        :teams="step2.teams"
        :days="weekDays"
        :formations="step2.formations"
        :formationRequired="formationRequired"
        :repassModes="repassModes"
        :confirmDelete="confirmDeleteRule"
        :copyRule="copyRule"
      />
    </div>

    <template #footer>
      <div class="flex justify-between items-center w-full px-4">
        <OutlinedButton text="Voltar para edição da escala" width="280px" @click="onClose" />

        <FilledButton
          :text="edit ? 'Salvar' : 'Criar'"
          width="120px"
          :loading="isSubmitting"
          @click="handleSubmit"
        />
      </div>
    </template>

    <modal-vue @close="showModalDelete = false" :modalActive="showModalDelete">
      <template #header>
        <p class="font-bold">Excluir regra da política de pagamento</p>
      </template>

      <p class="text-base font-normal">
        Tem certeza que deseja excluir esta regra?<br />Essa ação não poderá ser desfeita!
      </p>

      <template #footer>
        <div class="flex justify-center gap-4">
          <OutlinedButton width="119px" @click="showModalDelete = false" text="Cancelar" />
          <filled-button
            :loading="isDeleting"
            text="Excluir"
            width="119px"
            @click="handleDeletePaymentPolicy"
          />
        </div>
      </template>
    </modal-vue>
  </modal-vue>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, toRef, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useCreateScheduleStore } from '@/pinia-store/useCreateScheduleStore'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import { ValidationError } from 'yup'

import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import PaymentPolicyRulesTable from '@/components/ManageScale/Schedule/PaymentPolicyRulesTable.vue'
import PolicyVigencyFields from '@/components/ManageScale/Schedule/PolicyVigencyFields.vue'
import Spinner from '@/components/others/Spinner.vue'

import Network from '@/Network'
import PaymentPolicyService from '@/Network/Services/DsPaymentPolicyService'
import type { CreatePaymentPolicyParams } from '@/Network/Types/Requests/DsPaymentPolicyRequests'
import type {
  PaymentPolicyByIdResponse,
  UpdatePaymentPolicyParams
} from '@/Network/Types/Responses/DsPaymentPolicyResponses'

import { mdiPlus, mdiPencilOutline, mdiCheck, mdiClose, mdiLock } from '@mdi/js'
import { vigencySchema } from '@/components/ManageScale/Schedule/schemas'
import { useScheduleMaxDate } from '@/hooks/useScheduleMaxDate'
import { parseLocalDate, toDateOnly, weekDays } from '@/utils/date'
import { buildPolicyLinkPayload, pickLatestPolicyLink } from '@/utils/paymentPolicyLink'
import SVGIcon from '@/components/SVGIcon.vue'
import {
  getPostStationApprovedMessage,
  isPostStationApprovedError
} from '@/modules/company/PostStation/errors'

const props = defineProps<{
  visible: boolean
  edit?: boolean
  formationRequired?: boolean
  policy?: PaymentPolicyByIdResponse | null
  scheduleId: number
  scheduleName?: string
  scheduleDetails: {
    name: string
    healthUnitName: string
    companyName: string
  }
  scheduleEndDate?: string | null
}>()

const edit = computed(() => props.edit ?? false)

const { scheduleMaxDate } = useScheduleMaxDate(toRef(props, 'scheduleEndDate'))

const store = useCreateScheduleStore()
const { step4, step2 } = storeToRefs(store)

const service = new PaymentPolicyService()

// `toast` permanece para as validações locais (regras, nome, vigência) — erros
// vindos da API passam pelo `notifyError` (rule `error-handling.md`).
const toast = useToast()
const { notifyError } = useErrorToast()
const isSubmitting = ref(false)

/**
 * Mensagem da trava do Pós-Plantão (RN-07 / RF-12).
 *
 * Reativa ao 400, não preventiva: a regra do backend é "a mudança alcança data
 * já coberta por plantão aprovado", que esta tela não consegue prever — não há
 * um `stationId` aqui, e um booleano por escala travaria também vigência 100%
 * futura, que é a operação legítima.
 *
 * Banner persistente além do toast: a frase do backend cita o período afetado e
 * a contagem, e o passo visível pode não ser o da vigência.
 */
const postStationLockMessage = ref<string | null>(null)
const isEditing = ref(false)
const tempPolicyName = ref('')
const showModalDelete = ref(false)
const ruleIndexToDelete = ref<number | null>(null)
const isDeleting = ref(false)
const nameInput = ref<HTMLInputElement | null>(null)
const isInvalid = ref(false)
const vigencyErrors = ref<Record<string, string>>({})

const clearVigencyError = (field: 'startDate' | 'endDate') => {
  delete vigencyErrors.value[field]
}
const emit = defineEmits<{
  (e: 'close'): void
  (e: 'updated'): void
  (e: 'created'): void
}>()

const repassModes = [
  { label: 'À vista', value: 'IMMEDIATE' },
  { label: 'Fluxo', value: 'FLOW' }
]

const breadcrumb = computed(() => {
  if (!props.scheduleDetails) return ''
  const { companyName, healthUnitName, name } = props.scheduleDetails
  return `${companyName} > ${healthUnitName} > ${name}`
})

const onClose = () => emit('close')

const formatHourValueForInput = (hourValue: string): string => {
  const parsed = parseFloat(hourValue)
  return Number.isFinite(parsed) ? String(parsed) : hourValue
}

function mapApiRulesToStore(policy: PaymentPolicyByIdResponse) {
  const mappedRules = policy.rules.map((rule) => {
    return {
      days: rule.ruleWeekdays.map((w) => {
        const weekday = step2.value.days.find((d) => d.value === w.weekday)
        return weekday ?? { title: 'Desconhecido', value: w.weekday }
      }),
      shifts: rule.ruleShifts.map((s) => {
        const shift = step2.value.shifts.find((opt) => opt.value === s.shift.id)
        return shift ?? { title: s.shift.name, value: s.shift.id }
      }),
      positions: rule.rulePositions.map((p) => {
        const pos = step2.value.positions.find((opt) => opt.value === p.position.id)
        return pos ?? { title: p.position.name, value: p.position.id }
      }),
      formations: rule.ruleFormations.map((f) => {
        const formation = step2.value.formations.find(
          (opt) => opt.value === f.dsPaymentDoctorFormation.id
        )
        return (
          formation ?? {
            title: f.dsPaymentDoctorFormation.name,
            value: f.dsPaymentDoctorFormation.id
          }
        )
      }),
      teams: rule.isOurTeamValues.map((isInternal) => {
        const val = isInternal ? 'INTERNAL_TEAM' : 'EXTERNAL_TEAM'
        return (
          step2.value.teams.find((t) => t.value === val) ?? {
            title: isInternal ? 'Interno' : 'Externo',
            value: val
          }
        )
      }),
      repassMode: rule.paymentTiming as 'IMMEDIATE' | 'FLOW',
      dutyValue: formatHourValueForInput(rule.hourValue)
    }
  })

  tempPolicyName.value = policy.name
  step4.value.name = policy.name
  step4.value.rules = mappedRules

  // Sempre sobrescreve os campos de vigencia para evitar estado obsoleto
  // de uma abertura anterior do modal.
  const link = pickLatestPolicyLink(policy.links)
  step4.value.startDate = link ? parseLocalDate(toDateOnly(link.startDate)) : null
  step4.value.endDate = link?.endDate ? parseLocalDate(toDateOnly(link.endDate)) : null
  step4.value.indefiniteEndDate = link ? link.endDate === null : false
}

const loadOptions = async () => {
  try {
    const [positions, shifts, formations] = await Promise.all([
      Network.position.getByScheduleId(props.scheduleId),
      Network.shift.getByScheduleId(props.scheduleId),
      Network.paymentFormation.getByScheduleId(props.scheduleId)
    ])

    store.setShifts(
      shifts.data
        .filter((p) => p.isActive)
        .map((shift: any) => ({
          title: shift.name,
          value: shift.id
        }))
    )

    store.setPositions(
      positions.data
        .filter((p) => p.isActive)
        .map((pos: any) => ({
          title: pos.name,
          value: pos.id
        }))
    )

    store.setFormations(
      formations.data
        .filter((p) => p.isActive)
        .map((f: any) => ({
          title: f.name,
          value: f.id
        }))
    )

    store.setTeams([
      { title: 'Interno', value: 'INTERNAL_TEAM' },
      { title: 'Externo', value: 'EXTERNAL_TEAM' }
    ])

    store.setDays(weekDays)
  } catch (error) {
    notifyError(error, 'Não foi possível carregar os turnos, posições e formações da escala.')
    console.error('Erro ao carregar shifts/positions/formations:', error)
  }
}

watch(
  () => props.visible,
  async (isVisible) => {
    if (isVisible) {
      store.resetStep2()
      store.resetStep3()
      vigencyErrors.value = {}

      await loadOptions()

      if (edit.value && props.policy) {
        mapApiRulesToStore(props.policy)
        isEditing.value = false
      } else {
        // Reset defensivo (o pai tambem reseta) ANTES da logica de nome
        // abaixo, pois resetStep4() define um nome default que deve ser
        // sobrescrito pelo nome vazio do fluxo de criacao.
        store.resetStep4()
        const defaultName = ''
        store.setPolicyName(defaultName)
        tempPolicyName.value = defaultName
        isEditing.value = true
        await nextTick()
        nameInput.value?.focus()
      }
    }
  }
)

const confirmDeleteRule = (index: number) => {
  ruleIndexToDelete.value = index
  showModalDelete.value = true
}

const handleSubmit = () => {
  edit.value ? updatePaymentPolicy() : createPaymentPolicy()
}

// eslint-disable-next-line complexity, sonarjs/cognitive-complexity
const validateRules = (): boolean => {
  if (!step4.value.rules.length) {
    toast.error('Adicione pelo menos uma regra de pagamento.')
    return false
  }

  for (const [index, rule] of step4.value.rules.entries()) {
    if (!rule.days?.length) {
      toast.error(`Regra ${index + 1}: Selecione pelo menos um dia da semana.`)
      return false
    }
    if (!rule.shifts?.length) {
      toast.error(`Regra ${index + 1}: Selecione pelo menos um turno.`)
      return false
    }
    if (!rule.positions?.length) {
      toast.error(`Regra ${index + 1}: Selecione pelo menos uma posição.`)
      return false
    }
    if (!rule.teams?.length) {
      toast.error(`Regra ${index + 1}: Selecione pelo menos um time.`)
      return false
    }
    if (props.formationRequired && !rule.formations?.length) {
      toast.error(`Regra ${index + 1}: Selecione pelo menos uma formação.`)
      return false
    }
    if (!rule.repassMode) {
      toast.error(`Regra ${index + 1}: Selecione um modelo de repasse.`)
      return false
    }
    const valor = Number(rule.dutyValue?.replace?.(',', '.') || rule.dutyValue)
    if (!valor || isNaN(valor)) {
      toast.error(`Regra ${index + 1}: Informe um valor válido para o plantão.`)
      return false
    }
  }
  return true
}

// Ordem de exibicao dos erros de vigencia (um toast por falha), preservando
// a precedencia da validacao anterior. As REGRAS em si vivem apenas em
// vigencySchema (fonte unica compartilhada com o wizard de criacao).
const VIGENCY_ERROR_PRIORITY: ReadonlyArray<readonly ['startDate' | 'endDate', string]> = [
  ['startDate', 'required'],
  ['endDate', 'required'],
  ['startDate', 'max-schedule-end-date'],
  ['endDate', 'min'],
  ['endDate', 'max-schedule-end-date']
]

const pickFirstVigencyError = (errors: ValidationError[]): ValidationError => {
  for (const [path, type] of VIGENCY_ERROR_PRIORITY) {
    const match = errors.find((e) => e.path === path && e.type === type)
    if (match) return match
  }
  return errors[0]
}

const validateVigency = (): boolean => {
  const { startDate, endDate, indefiniteEndDate } = step4.value

  // Reset total a cada tentativa: sem isso, um erro de tentativa anterior
  // (ex: startDate) ficaria marcado mesmo depois de corrigido via calendario.
  vigencyErrors.value = {}

  try {
    // null → undefined: com null o yup dispara typeError (mensagem default
    // em ingles) antes do required; com undefined vale a mensagem pt-BR.
    vigencySchema.validateSync(
      { startDate: startDate ?? undefined, endDate: endDate ?? undefined, indefiniteEndDate },
      { abortEarly: false, context: { scheduleEndDate: props.scheduleEndDate ?? null } }
    )
    return true
  } catch (err) {
    if (!(err instanceof ValidationError)) {
      console.error(err)
      return false
    }
    const first = pickFirstVigencyError(err.inner.length ? err.inner : [err])
    const field = first.path === 'endDate' ? 'endDate' : 'startDate'
    vigencyErrors.value[field] = first.message
    toast.error(first.message)
    return false
  }
}

// true = time interno no backend; a comparacao explicita evita o bug de
// Boolean(item.value) marcar todo time como interno (string sempre truthy).
// Virgula decimal (formato BR) precisa virar ponto antes do parseFloat,
// senao '100,50' persiste como 100.
const buildRulesPayload = (): CreatePaymentPolicyParams['rules'] =>
  step4.value.rules.map((rule) => ({
    weekdays: rule.days.map((item) => item.value),
    shiftIds: rule.shifts.map((item) => item.value),
    positionIds: rule.positions.map((item) => item.value),
    isOurTeamValues: rule.teams.map((item) => item.value === 'INTERNAL_TEAM'),
    formationIds: rule.formations.map((item) => item.value),
    paymentTiming: rule.repassMode,
    hourValue: parseFloat(String(rule.dutyValue).replace(',', '.'))
  }))

const createPaymentPolicy = async () => {
  postStationLockMessage.value = null

  if (!tempPolicyName?.value.trim()) {
    toast.error('Preencha o nome da política.')
    return
  }

  if (!validateRules()) return
  if (!validateVigency()) return

  isSubmitting.value = true
  try {
    const payload: CreatePaymentPolicyParams = {
      name: tempPolicyName.value,
      scheduleId: props.scheduleId,
      link: buildPolicyLinkPayload({
        startDate: step4.value.startDate!,
        endDate: step4.value.endDate,
        indefiniteEndDate: step4.value.indefiniteEndDate
      }),
      rules: buildRulesPayload()
    }

    await service.create(props.scheduleId, payload)
    toast.success('Política de pagamento criada com sucesso!')

    emit('created')
  } catch (err) {
    console.error(err)

    if (isPostStationApprovedError(err)) {
      postStationLockMessage.value = getPostStationApprovedMessage(err)
      toast.error(postStationLockMessage.value)
      return
    }

    notifyError(err, 'Não foi possível criar a política de pagamento.')
  } finally {
    isSubmitting.value = false
  }
}

const updatePaymentPolicy = async () => {
  postStationLockMessage.value = null

  if (!props.policy) return
  if (!validateRules()) return
  if (!validateVigency()) return

  isSubmitting.value = true
  try {
    const payload: UpdatePaymentPolicyParams = {
      name: step4.value.name,
      link: buildPolicyLinkPayload({
        startDate: step4.value.startDate!,
        endDate: step4.value.endDate,
        indefiniteEndDate: step4.value.indefiniteEndDate
      }),
      rules: buildRulesPayload()
    }

    await service.update(props.policy.id, props.scheduleId, payload)
    toast.success('Política de pagamento atualizada com sucesso!')

    emit('updated')
  } catch (err) {
    console.error(err)

    // Editar a política reescreve TODAS as regras dela, então o backend recusa
    // quando a cobertura alcança plantão já aprovado.
    if (isPostStationApprovedError(err)) {
      postStationLockMessage.value = getPostStationApprovedMessage(err)
      toast.error(postStationLockMessage.value)
      return
    }

    // O backend retorna 400 com mensagens pt-BR significativas para
    // conflitos de vigencia (sobreposicao/lacuna) — o `notifyError` repassa
    // essas mensagens e só cai no fallback quando não há texto exibível.
    notifyError(err, 'Não foi possível atualizar a política de pagamento.')
  } finally {
    isSubmitting.value = false
  }
}

const handleDeletePaymentPolicy = async () => {
  if (ruleIndexToDelete.value === null) return

  isDeleting.value = true
  try {
    store.removeRule(ruleIndexToDelete.value)
    toast.success('Política de plantão excluída com sucesso!')
  } catch (err) {
    toast.error('Erro ao excluir a política.')
  } finally {
    showModalDelete.value = false
    isDeleting.value = false
    ruleIndexToDelete.value = null
  }
}

const copyRule = async (index: number) => {
  store.copyRule(index)
  toast.success('Regra duplicada com sucesso!')
}

const startEditing = async () => {
  tempPolicyName.value = step4.value.name
  isEditing.value = true
  isInvalid.value = false
  await nextTick()
  nameInput.value?.focus()
}

const saveEdit = () => {
  if (!tempPolicyName.value.trim()) {
    toast.error('Nome inválido.')
    isInvalid.value = true
    return
  }
  store.setPolicyName(tempPolicyName.value)
  isEditing.value = false
  isInvalid.value = false
}

const cancelEdit = () => {
  tempPolicyName.value = step4.value.name
  isEditing.value = false
  isInvalid.value = false
}
</script>
